export const TECHNICAL_FILTERS = [
  {
    key: 'watts',
    query: 'watts',
    apiFilter: 'wattsRange',
    steps: [0.1, 1],
    unit: 'W',
    i18n: 'watts',
    supersedes: [26],
  },
  {
    key: 'lumens',
    query: 'lumens',
    apiFilter: 'lumensRange',
    steps: [10, 100],
    unit: 'lm',
    i18n: 'lumens',
    supersedes: [22],
  },
  {
    key: 'color_temperature',
    query: 'kelvins',
    apiFilter: 'colorTemperatureRange',
    steps: [100],
    unit: 'K',
    i18n: 'colorTemperature',
    supersedes: [42, 28],
  },
]

export const TECHNICAL_FILTER_QUERY_KEYS = TECHNICAL_FILTERS.map(
  filter => filter.query
)

const MIN_NOTCHES = 10

const MIN_STEPS_SHOWN = 2

const decimals = step => (String(step).split('.')[1] || '').length

const onStep = (value, step) =>
  step ? Number(value.toFixed(decimals(step))) : value

const stepsIn = (value, step) => Number((value / step).toFixed(6))

const isRange = range =>
  !!range && Number.isFinite(range.min) && Number.isFinite(range.max)

const toNumber = part => {
  if (part === undefined || part.trim() === '') return null
  const number = Number(part)
  return Number.isFinite(number) ? number : null
}

export function technicalFilterStep(range, steps) {
  const spread = isRange(range) ? range.max - range.min : 0
  const fitting = steps.filter(step => stepsIn(spread, step) >= MIN_NOTCHES)

  return fitting.length ? fitting[fitting.length - 1] : steps[0]
}

export function technicalFilterBounds(range, step) {
  if (!isRange(range)) return null

  const min = onStep(Math.floor(stepsIn(range.min, step)) * step, step)
  const max = onStep(Math.ceil(stepsIn(range.max, step)) * step, step)

  return stepsIn(max - min, step) >= MIN_STEPS_SHOWN ? { min, max } : null
}

export function technicalFilterScale(ranges, filter) {
  const range = ranges?.[filter.key]
  const step = technicalFilterStep(range, filter.steps)

  return { range, step, bounds: technicalFilterBounds(range, step) }
}

export function supersededFilterIds(ranges) {
  return TECHNICAL_FILTERS.filter(
    filter => technicalFilterScale(ranges, filter).bounds
  ).flatMap(filter => filter.supersedes)
}

export function snapToStep(value, bounds, step) {
  const snapped =
    bounds.min + Math.round(stepsIn(value - bounds.min, step)) * step

  return onStep(Math.min(bounds.max, Math.max(bounds.min, snapped)), step)
}

export function parseRangeQuery(value) {
  if (typeof value !== 'string') return null

  const [low, high] = value.split(',')
  const range = [toNumber(low), toNumber(high)]

  return range[0] === null && range[1] === null ? null : range
}

export function clampRange(range, bounds) {
  const [low, high] = range || [null, null]
  const clamp = number => Math.min(bounds.max, Math.max(bounds.min, number))
  const from = clamp(low ?? bounds.min)
  const to = clamp(high ?? bounds.max)

  return from <= to ? [from, to] : [to, from]
}

export function rangeQueryValue(range, bounds, step) {
  const [low, high] = range.map(value => onStep(value, step))
  if (low <= bounds.min && high >= bounds.max) return undefined

  return `${low},${high}`
}

export function technicalFilterApiParams(query) {
  return Object.fromEntries(
    TECHNICAL_FILTERS.map(filter => {
      const range = parseRangeQuery(query?.[filter.query])
      return [
        `filter[${filter.apiFilter}]`,
        range ? range.map(bound => bound ?? '').join(',') : undefined,
      ]
    })
  )
}

export function formatTechnicalNumber(value, filter, locale) {
  return value.toLocaleString(locale, {
    useGrouping: filter.key === 'lumens',
    maximumFractionDigits: 2,
  })
}

export function formatTechnicalValue(value, filter, locale) {
  return `${formatTechnicalNumber(value, filter, locale)} ${filter.unit}`
}

export function activeTechnicalFiltersCount(query) {
  return TECHNICAL_FILTERS.filter(filter =>
    parseRangeQuery(query?.[filter.query])
  ).length
}

export function technicalRangeSummary([low, high], format) {
  if (low !== null && high !== null) return `${format(low)} – ${format(high)}`

  return low !== null ? `≥ ${format(low)}` : `≤ ${format(high)}`
}

export function technicalRangeShortSummary([low, high], filter, locale) {
  const number = value => formatTechnicalNumber(value, filter, locale)
  const unit = `\u00a0${filter.unit}`

  if (low !== null && high !== null) {
    return low === high
      ? `${number(low)}${unit}`
      : `${number(low)}\u2013${number(high)}${unit}`
  }

  return low !== null
    ? `≥\u00a0${number(low)}${unit}`
    : `≤\u00a0${number(high)}${unit}`
}

export function hasPendingTechnicalChanges(query, pending) {
  return Object.entries(pending || {}).some(
    ([key, value]) => (query?.[key] || undefined) !== value
  )
}

const RANGES_INDEPENDENT_QUERY_KEYS = [
  'page',
  'sort',
  ...TECHNICAL_FILTER_QUERY_KEYS,
]

export function technicalRangesContext(path, query) {
  const entries = Object.keys(query || {})
    .filter(key => !RANGES_INDEPENDENT_QUERY_KEYS.includes(key))
    .map(key => [key, query[key]])
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))

  return JSON.stringify([path || '', entries])
}
